// src/components/ImmigrationBlog.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance'; // Import axiosInstance
import {
  Card, CardContent, CardActions, Typography, Button,
  Grid, Box, Paper, Autocomplete, TextField, Select, MenuItem, Chip,
  FormControl, InputLabel, Skeleton, Alert
} from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher'; // Import the new component
import PromiseAnimation from './PromiseAnimation'; // Import the animation component

const ARTICLES_PER_PAGE = 15; // Number of articles to fetch per page

// --- Reusable Article Card Component ---
const ArticleCard = React.memo(({ article, language }) => {
  const handleReadMore = () => {
    // Append the language as a query parameter
    window.open(`/blog/${article.slug}?lang=${language}`, '_blank');
  };

  // Safely format date, assuming 'publishedDate' exists based on ArticleDetail
  const formattedDate = article.publishedDate
    ? new Date(article.publishedDate).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    : 'Date unavailable';

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', mb: { xs: 1.5, sm: 2 } }}>
      <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h6" component="div" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {article.title || 'Untitled Article'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {formattedDate} {article.author && `by ${article.author}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {article.excerpt || 'No excerpt available.'}
        </Typography>
        {article.tags && article.tags.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {article.tags.map(tag => (
              <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Button size="small" onClick={handleReadMore} disabled={!article.slug}>
          Read More
        </Button>
      </CardActions>
    </Card>
  );
});

// --- Main Blog Component ---
const ImmigrationBlog = () => {
  const [articles, setArticles] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableAuthors, setAvailableAuthors] = useState([]);

  // --- State for Filters, Sort, Pagination ---
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [sortOrder, setSortOrder] = useState('publishedDate:desc');
  const [language, setLanguage] = useState('en'); // Add language state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // --- State for Loading and Errors ---
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const observer = useRef();

  // --- Fetching Logic (for loading MORE pages via Infinite Scroll) ---
  const fetchMoreArticles = useCallback(async (currentPage) => {
    setLoadingMore(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', currentPage);
    params.append('limit', ARTICLES_PER_PAGE);
    params.append('sort', sortOrder);
    params.append('lang', language); // Add language to API call

    if (selectedTags.length > 0) {
      selectedTags.forEach(tag => params.append('tags', tag));
    }
    if (selectedAuthor) {
      params.append('author', selectedAuthor);
    }

    try {
      const apiUrl = `/api/immigration-articles?${params.toString()}`;
      const response = await axiosInstance.get(apiUrl);
      const data = response.data;

      if (!Array.isArray(data)) {
          console.error("API did not return an array:", data);
          throw new Error("Unexpected response format from server.");
      }

      setArticles(prevArticles => {
        const newArticles = data.filter(newArt => !prevArticles.some(prevArt => prevArt._id === newArt._id));
        return [...prevArticles, ...newArticles];
      });

      setHasMore(data.length === ARTICLES_PER_PAGE);

    } catch (err) {
      console.error('Error fetching more articles:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch articles.';
      setError(errorMessage);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [selectedTags, selectedAuthor, sortOrder, language]);

  // --- Effect for Initial Load & Filter/Sort Changes (Fetches Page 1) ---
  useEffect(() => {
    const fetchInitialPage = async () => {
        setArticles([]);
        setPage(1);
        setHasMore(true);
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.append('page', 1);
        params.append('limit', ARTICLES_PER_PAGE);
        params.append('sort', sortOrder);
        params.append('lang', language); // Add language to API call

        if (selectedTags.length > 0) {
          selectedTags.forEach(tag => params.append('tags', tag));
        }
        if (selectedAuthor) {
            params.append('author', selectedAuthor);
        }

        try {
            const apiUrl = `/api/immigration-articles?${params.toString()}`;
            const response = await axiosInstance.get(apiUrl);
            const data = response.data;

            if (!Array.isArray(data)) {
                console.error("API did not return an array:", data);
                throw new Error("Unexpected response format from server.");
            }

            setArticles(data);
            setHasMore(data.length === ARTICLES_PER_PAGE);

            const currentTags = new Set();
            const currentAuthors = new Set(['']);
            data.forEach(article => {
                article.tags?.forEach(tag => currentTags.add(tag));
                if (article.author) currentAuthors.add(article.author);
            });
            setAvailableTags(Array.from(currentTags).sort());
            setAvailableAuthors(Array.from(currentAuthors).sort((a, b) => a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));

        } catch (err) {
            console.error('Error fetching initial articles:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch articles.';
            setError(errorMessage);
            setArticles([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    fetchInitialPage();
  }, [selectedTags, selectedAuthor, sortOrder, language]);

  // --- Infinite Scroll Logic ---
  const lastArticleElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchMoreArticles(nextPage);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore, page, fetchMoreArticles]);


  // --- Twitter Widget Loading ---
  //  useEffect(() => {
  //    const scriptId = 'twitter-widgets-script';
  //    const twitterScriptUrl = 'https://platform.twitter.com/widgets.js';
  //    if (document.getElementById(scriptId)) {
  //      if (window.twttr && window.twttr.widgets) window.twttr.widgets.load();
  //      return;
  //    }
  //    const script = document.createElement('script');
  //    script.id = scriptId;
  //    script.src = twitterScriptUrl;
  //    script.async = true;
  //    script.charset = 'utf-8';
  //    document.body.appendChild(script);
  //  }, []);

  // --- Event Handlers ---
  const handleTagChange = (event, newValue) => {
    setSelectedTags(newValue);
  };

  const handleAuthorChange = (event) => {
    setSelectedAuthor(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    // Reset articles and page count when language changes
    setArticles([]);
    setPage(1);
    setHasMore(true);
  };

  // --- Render Logic ---
  return (
    <Box sx={{ padding: { xs: 0, sm: 2 } }}>
      {/* --- Summary Card --- */}
      <Card className="myCard" sx={{ mb: 3, p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={3}>

        {/* --- Articles Section (Main Content) --- */}
        <Grid item xs={12} md={8}>
          <Typography variant="h4" gutterBottom component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' } }}>
            Immigration Articles & News
          </Typography>

          {/* Filter and Sort Controls */}
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderTop: '3px solid var(--color-accent-red)' }} elevation={2}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              {/* --- Filter and Sort Controls --- */}
              <Grid item xs={12} md>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Autocomplete
                        multiple
                        id="tags-filter"
                        options={availableTags}
                        value={selectedTags}
                        onChange={handleTagChange}
                        renderInput={(params) => (
                          <TextField {...params} label="Filter by Tags" placeholder="Tags" />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="author-filter-label">Author</InputLabel>
                      <Select
                        labelId="author-filter-label"
                        id="author-filter"
                        value={selectedAuthor}
                        onChange={handleAuthorChange}
                        label="Author"
                      >
                        <MenuItem value=""><em>All Authors</em></MenuItem>
                        {availableAuthors.filter(a => a !== '').map(author => (
                          <MenuItem key={author} value={author}>{author}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="sort-order-label">Sort By</InputLabel>
                      <Select
                        labelId="sort-order-label"
                        id="sort-order"
                        value={sortOrder}
                        onChange={handleSortChange}
                        label="Sort By"
                      >
                        <MenuItem value="publishedDate:desc">Date (Newest First)</MenuItem>
                        <MenuItem value="publishedDate:asc">Date (Oldest First)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* --- Language Switcher --- */}
              <Grid item xs={12} md="auto" sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'initial' } }}>
                <LanguageSwitcher
                  currentLanguage={language}
                  onLanguageChange={handleLanguageChange}
                  availableLangs={['en', 'zh', 'hi', 'es', 'ar']}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Error Display */}
          {error && <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>{error}</Alert>}

          {/* Article List / Loading Skeletons */}
          <Box>
            {loading && articles.length === 0 && (
              Array.from(new Array(5)).map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={150} sx={{ mb: { xs: 1.5, sm: 2 } }} />
              ))
            )}

            {!loading && articles.length === 0 && !error && (
                 <Typography sx={{ mt: { xs: 2, sm: 3 }, textAlign: 'center' }}>No articles found matching your criteria.</Typography>
            )}

            {articles.map((article, index) => {
              if (articles.length === index + 1) {
                return (
                  <div ref={lastArticleElementRef} key={article._id || index}>
                    <ArticleCard article={article} language={language} />
                  </div>
                );
              } else {
                return <ArticleCard key={article._id || index} article={article} language={language} />;
              }
            })}

            {loadingMore && (
               Array.from(new Array(3)).map((_, index) => (
                 <Skeleton key={`loader-${index}`} variant="rectangular" height={100} sx={{ mb: { xs: 1.5, sm: 2 }, mt: { xs: 0.5, sm: 1 } }} />
               ))
            )}

            {!hasMore && !loading && articles.length > 0 && (
              <Typography sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2 }, color: 'text.secondary' }}>
                You've reached the end.
              </Typography>
            )}
          </Box>
        </Grid>

        {/* --- Promise Section --- */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: { xs: '60px', sm: '70px', md: '80px' }, p: { xs: 1, sm: 2 } }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', borderTop: '3px solid var(--color-accent-blue)' }}>
              <PromiseAnimation />
              <Typography
                variant="h6"
                component="p"
                sx={{
                  mt: 2,
                  mb: 1,
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  lineHeight: 1.4,
                  color: 'text.primary'
                }}
              >
                A Promise? Yeah, Like That's Gonna Happen!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  color: 'text.secondary'
                }}
              >
                "If the TrackCOPR app reaches 5,000 active users across Android and iOS, I'll train a custom chatbot with LoRA techniques to be as knowledgeable as an immigration consultant. This space is reserved for that amazing agent."
              </Typography>
            </Paper>
          </Box>
        </Grid>

      </Grid>
    </Card>
    </Box>
  );
};

export default ImmigrationBlog;
